import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CategoryCombobox from "@/components/CategoryCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Trash2, RefreshCw, Loader2, Pencil, FileText, Image as ImageIcon,
  Video as VideoIcon, File as FileIcon, MoreHorizontal, Archive, ArchiveRestore,
  CheckCircle2, FileEdit, Tag as TagIcon,
} from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";

const TEMPLATE_TYPES = [
  { value: "text", label: "Text", icon: FileText, tone: "text-slate-600" },
  { value: "image", label: "Image", icon: ImageIcon, tone: "text-sky-600" },
  { value: "video", label: "Video", icon: VideoIcon, tone: "text-violet-600" },
  { value: "document", label: "Document", icon: FileIcon, tone: "text-amber-600" },
];

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const emptyForm = {
  name: "", category: "Marketing", template_type: "text",
  body: "", parameter_labels: [], status: "active",
};

function extractPositions(body) {
  const out = new Set();
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m;
  while ((m = re.exec(body)) !== null) out.add(parseInt(m[1], 10));
  return Array.from(out).sort((a, b) => a - b);
}

export default function Templates() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const confirm = useConfirm();

  const reloadCategories = async () => {
    const { data } = await api.get("/template-categories");
    setCategories(data);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (typeFilter !== "all") params.template_type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [tplRes, statsRes] = await Promise.all([
        api.get("/templates", { params }),
        api.get("/templates/stats"),
      ]);
      setItems(tplRes.data);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, typeFilter, statusFilter]);

  useEffect(() => { reloadCategories(); }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (tpl) => {
    setForm({
      name: tpl.name,
      category: tpl.category || "Marketing",
      template_type: tpl.template_type || "text",
      body: tpl.body || "",
      parameter_labels: tpl.parameter_labels || [],
      status: tpl.status || "active",
    });
    setEditing(tpl);
    setOpen(true);
  };

  // Auto-sync parameter_labels with body {{N}} placeholders
  const bodyPositions = useMemo(() => extractPositions(form.body), [form.body]);
  const syncedLabels = useMemo(() => {
    return bodyPositions.map((pos) => {
      const existing = form.parameter_labels.find((p) => p.position === pos);
      return { position: pos, label: existing?.label || "" };
    });
  }, [bodyPositions, form.parameter_labels]);

  const updateLabel = (position, label) => {
    setForm((f) => {
      const positions = extractPositions(f.body);
      const map = new Map(f.parameter_labels.map((p) => [p.position, p.label]));
      map.set(position, label);
      const next = positions.map((p) => ({ position: p, label: map.get(p) || "" }));
      return { ...f, parameter_labels: next };
    });
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Template name required");
    if (!form.body.trim()) return toast.error("Template body required");
    const positions = extractPositions(form.body);
    const labels = positions.map((p) => {
      const existing = form.parameter_labels.find((l) => l.position === p);
      return { position: p, label: (existing?.label || "").trim() };
    });
    const missing = labels.filter((l) => !l.label);
    if (missing.length) return toast.error(`Label missing for {{${missing[0].position}}}`);
    try {
      const payload = { ...form, parameter_labels: labels };
      if (editing) {
        await api.patch(`/templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/templates", payload);
        toast.success("Template created");
      }
      setOpen(false);
      setEditing(null);
      load();
      reloadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    }
  };

  const changeStatus = async (tpl, status) => {
    await api.patch(`/templates/${tpl.id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const remove = async (tpl) => {
    const ok = await confirm({
      title: `Delete template "${tpl.name}"?`,
      description: "This will permanently remove the template. Existing broadcasts referencing it are unaffected.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/templates/${tpl.id}`);
    load();
    toast.success("Deleted");
  };

  const syncMeta = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/templates/sync");
      toast.success(`Synced ${data.synced} templates from Meta`);
      load();
    } catch {
      toast.info("Configure WhatsApp settings to sync from Meta");
    } finally {
      setSyncing(false);
    }
  };

  const typeMeta = (type) => TEMPLATE_TYPES.find((t) => t.value === type) || TEMPLATE_TYPES[0];

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="templates-page">
      <PageHeader
        title="Templates"
        description="Define reusable message structure. Media is uploaded per broadcast."
        actions={
          <>
            <Button variant="outline" onClick={syncMeta} disabled={syncing} data-testid="templates-sync-button">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><RefreshCw className="w-4 h-4 mr-1.5" />Sync from Meta</>)}
            </Button>
            <Button onClick={openCreate} className="bg-brand hover:bg-brand-dark" data-testid="template-new-button">
              <Plus className="w-4 h-4 mr-1.5" />New template
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard icon={FileText} label="Total" value={stats.total} testId="tpl-stat-total" />
        <StatCard icon={CheckCircle2} label="Active" value={stats.by_status?.active || 0} tone="text-emerald-600" testId="tpl-stat-active" />
        <StatCard icon={FileEdit} label="Draft" value={stats.by_status?.draft || 0} tone="text-amber-600" testId="tpl-stat-draft" />
        <StatCard icon={Archive} label="Archived" value={stats.by_status?.archived || 0} tone="text-slate-400" testId="tpl-stat-archived" />
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-0">
          <Card className="border-slate-200 shadow-none mb-4">
            <div className="p-4 flex flex-wrap items-center gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-52" data-testid="tpl-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.filter((c) => c.is_active !== false).map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40" data-testid="tpl-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {TEMPLATE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="tpl-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto text-xs text-slate-400" data-testid="tpl-result-count">
                {items.length} {items.length === 1 ? "template" : "templates"}
              </div>
            </div>
          </Card>

          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : items.length === 0 ? (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-12 text-center text-sm text-slate-400">No templates match the current filters.</CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((t) => {
                const tm = typeMeta(t.template_type);
                const Icon = tm.icon;
                return (
                  <Card key={t.id} className="border-slate-200 shadow-none" data-testid={`template-card-${t.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${tm.tone} shrink-0`} />
                            <span className="font-heading font-semibold text-slate-900 truncate" data-testid={`tpl-name-${t.id}`}>{t.name}</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">{t.category || "—"}</Badge>
                            <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[t.status] || STATUS_STYLES.draft}`} data-testid={`tpl-status-${t.id}`}>{t.status}</Badge>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`tpl-more-${t.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(t)} data-testid={`tpl-edit-${t.id}`}>
                              <Pencil className="w-4 h-4 mr-2" />Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {t.status !== "active" && (
                              <DropdownMenuItem onClick={() => changeStatus(t, "active")} data-testid={`tpl-activate-${t.id}`}>
                                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />Mark active
                              </DropdownMenuItem>
                            )}
                            {t.status !== "draft" && (
                              <DropdownMenuItem onClick={() => changeStatus(t, "draft")} data-testid={`tpl-draft-${t.id}`}>
                                <FileEdit className="w-4 h-4 mr-2 text-amber-600" />Move to draft
                              </DropdownMenuItem>
                            )}
                            {t.status !== "archived" ? (
                              <DropdownMenuItem onClick={() => changeStatus(t, "archived")} data-testid={`tpl-archive-${t.id}`}>
                                <Archive className="w-4 h-4 mr-2" />Archive
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => changeStatus(t, "active")} data-testid={`tpl-unarchive-${t.id}`}>
                                <ArchiveRestore className="w-4 h-4 mr-2" />Restore
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => remove(t)} className="text-red-600" data-testid={`tpl-delete-${t.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mt-3 text-sm text-slate-600 whitespace-pre-wrap line-clamp-4" data-testid={`tpl-body-${t.id}`}>{t.body}</div>
                      {(t.parameter_labels || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1" data-testid={`tpl-labels-${t.id}`}>
                          {t.parameter_labels.map((p) => (
                            <span key={p.position} className="inline-flex items-center gap-1 text-[10px] bg-slate-50 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5 font-mono">
                              <TagIcon className="w-2.5 h-2.5" />{`{{${p.position}}}`} → {p.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories" className="mt-0">
          <TemplateCategoryManager categories={categories} reload={reloadCategories} reloadTemplates={load} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="welcome_message" className="mt-1.5" data-testid="template-name-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="template-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Category</Label>
                <div className="mt-1.5">
                  <CategoryCombobox
                    value={form.category}
                    onChange={(v) => setForm({ ...form, category: v })}
                    categories={categories}
                    onCategoriesChange={reloadCategories}
                    triggerTestId="template-category-combobox"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Type</Label>
                <Select value={form.template_type} onValueChange={(v) => setForm({ ...form, template_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="template-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`template-type-option-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">Determines which media uploader appears at broadcast time.</p>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Body</Label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={6}
                placeholder={"Hi {{1}}\nWelcome to Acme.\nYour offer code is {{2}}"}
                className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                data-testid="template-body-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">Use <code className="bg-slate-100 px-1 rounded">{`{{1}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{2}}`}</code>… for personalisation.</p>
            </div>

            {bodyPositions.length > 0 && (
              <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                <div className="text-xs font-semibold tracking-wider uppercase text-slate-600 mb-2">Parameter labels</div>
                <div className="space-y-2">
                  {syncedLabels.map((p) => (
                    <div key={p.position} className="grid grid-cols-[80px,1fr] gap-2 items-center">
                      <code className="text-xs bg-white border border-slate-200 px-2 py-1 rounded text-center font-mono">{`{{${p.position}}}`}</code>
                      <Input
                        value={p.label}
                        onChange={(e) => updateLabel(p.position, e.target.value)}
                        placeholder="e.g. Customer Name"
                        data-testid={`template-label-input-${p.position}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Labels make broadcast personalisation easy — instead of <code className="bg-white px-1 rounded">{`{{1}}`}</code>, your team sees <em>"Customer Name"</em>.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="template-cancel">Cancel</Button>
            <Button onClick={submit} className="bg-brand hover:bg-brand-dark" data-testid="template-submit">
              {editing ? "Save changes" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateCategoryManager({ categories, reload, reloadTemplates }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Category name required");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/template-categories/${editing.id}`, { name, is_active: form.is_active });
        toast.success("Category updated");
      } else {
        await api.post("/template-categories", { name, is_active: form.is_active });
        toast.success("Category created");
      }
      setOpen(false);
      reload();
      reloadTemplates?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    await api.patch(`/template-categories/${c.id}`, { is_active: !c.is_active });
    toast.success(c.is_active ? "Category disabled" : "Category enabled");
    reload();
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Delete category "${c.name}"?`,
      description: c.template_count
        ? `${c.template_count} template${c.template_count !== 1 ? "s" : ""} in this category will be moved to "Marketing".`
        : "This will permanently remove the category.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/template-categories/${c.id}`);
      toast.success("Category deleted");
      reload();
      reloadTemplates?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Card className="border-slate-200 shadow-none" data-testid="tpl-category-manager">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Template categories</h3>
          <p className="text-xs text-slate-500 mt-0.5">Group templates by purpose.</p>
        </div>
        <Button
          onClick={() => { setForm({ name: "", is_active: true }); setEditing(null); setOpen(true); }}
          className="bg-brand hover:bg-brand-dark"
          data-testid="tpl-category-new-button"
        >
          <Plus className="w-4 h-4 mr-1.5" />New category
        </Button>
      </div>
      <CardContent className="p-0">
        {categories.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No categories yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Category</th>
                  <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Templates</th>
                  <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Status</th>
                  <th className="text-right text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tpl-category-row-${c.id}`}>
                    <td className="p-3 font-medium text-slate-800">{c.name}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center bg-slate-100 text-slate-700 rounded-md px-2 py-0.5 text-xs" data-testid={`tpl-category-count-${c.id}`}>
                        {c.template_count ?? 0}
                      </span>
                    </td>
                    <td className="p-3">
                      {c.is_active ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm" variant="outline" className="h-8"
                          onClick={() => { setForm({ name: c.name, is_active: c.is_active }); setEditing(c); setOpen(true); }}
                          data-testid={`tpl-category-edit-${c.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8" data-testid={`tpl-category-more-${c.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => toggle(c)} data-testid={`tpl-category-toggle-${c.id}`}>
                              {c.is_active ? "Disable" : "Enable"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => remove(c)}
                              className="text-red-600"
                              disabled={c.name === "Marketing"}
                              data-testid={`tpl-category-delete-${c.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Name</Label>
              <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="tpl-category-name-input" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div>
                <Label className="text-sm font-medium text-slate-800">Active</Label>
                <p className="text-xs text-slate-500">Disabled categories are hidden from pickers.</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="tpl-category-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-brand hover:bg-brand-dark" disabled={saving} data-testid="tpl-category-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, tone = "text-slate-700", testId }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 flex items-center gap-3 bg-white" data-testid={testId}>
      <div className="w-9 h-9 rounded-md bg-slate-50 grid place-items-center">
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-slate-900 leading-tight">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}
